import React, { useState } from 'react';
import { Box } from '@mui/material';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import RegisterBox from '../components/Auth/Register/RegisterBox';
import SMSCodeVerification from '../components/Auth/SMSCodeVerification/SMSCodeVerification';
import AuthLayout from '../components/Auth/AuthLayout';
import useLoginBanner from '../hooks/useLoginBanner';
import defaultBanner from '/src/assets/images/auth/login-banner.png?url';
import defaultMobileBanner from '/src/assets/images/auth/login-banner-mobile.png?url';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const RECAPTCHA_ENABLED = import.meta.env.VITE_RECAPTCHA_ENABLED === 'true';

const Register: React.FC = () => {
  const { isRegistered } = useSelector((state: RootState) => state.auth);
  const { data: remoteBanner } = useLoginBanner();

  // The admin may not have uploaded a banner yet, and the request itself can
  // fail. Each image falls back to the bundled artwork — on a missing url, or
  // on a url that turns out not to load — so the split layout is never
  // half-empty and the form never waits on artwork.
  const [desktopFailed, setDesktopFailed] = useState(false);
  const [mobileFailed, setMobileFailed] = useState(false);

  const banner = {
    image:
      desktopFailed || !remoteBanner?.image ? defaultBanner : remoteBanner.image,
    mobileImage:
      mobileFailed || !remoteBanner?.mobileImage
        ? defaultMobileBanner
        : remoteBanner.mobileImage,
    // Only link out to artwork we actually got from the admin panel: the
    // bundled fallback is not what that link was set for.
    link: desktopFailed ? null : remoteBanner?.link ?? null,
    title: remoteBanner?.title ?? '',
  };

  // The artwork is the whole of the link, and it is aria-hidden, so without a
  // label this reads as an empty link to a screen reader. The admin's title is
  // that label; `cursor` is set here too because a Box gets none of an
  // anchor's usual affordances.
  const linkProps = banner.link
    ? {
        component: 'a' as const,
        href: banner.link,
        target: '_blank',
        rel: 'noopener noreferrer',
        'aria-label': banner.title || 'بنر تبلیغاتی',
      }
    : { component: 'div' as const };

  // Each Box below writes its own `sx`, so this cannot ride along in linkProps
  // — it would be overwritten by the spread that follows it.
  const linkSx = banner.link ? { cursor: 'pointer' } : {};

  const bannerImage = (
    <Box
      {...linkProps}
      sx={{
        ...linkSx,
        // Sized by the image, not the other way round: the <img> is scaled to
        // the full viewport height and this wrapper takes whatever width that
        // implies, so nothing is cropped and no gap is left underneath.
        display: 'block',
        height: '100%',
        width: '100%',
        '& img': {
          height: '100%',
          // 100%, not auto: the column is already sized to the artwork's
          // ratio, and letting the image round up to its own width left a
          // couple of pixels spilling past the column edge.
          width: '100%',
          display: 'block',
        },
      }}
    >
      <img
        src={banner.image}
        alt=""
        aria-hidden="true"
        onError={() => setDesktopFailed(true)}
      />
    </Box>
  );

  const mobileBannerImage = (
    <Box
      {...linkProps}
      sx={{ ...linkSx, display: 'block', width: '100%' }}
    >
      <img
        src={banner.mobileImage}
        alt=""
        aria-hidden="true"
        onError={() => setMobileFailed(true)}
      />
    </Box>
  );

  const content = (
    <AuthLayout banner={bannerImage} mobileBanner={mobileBannerImage}>
      {isRegistered ? <SMSCodeVerification /> : <RegisterBox />}
    </AuthLayout>
  );

  if (!RECAPTCHA_ENABLED) return content;

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
      language="fa"
      useRecaptchaNet={false}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: 'head',
      }}
    >
      {content}
    </GoogleReCaptchaProvider>
  );
};

export default Register;
